import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Academia e Esporte','Beleza Feminina','Beleza Masculina','Estética e Bem-estar',
  'Moda e Acessórios','Infantil e RN','Saúde','Saúde Veterinária',
  'Pet Shop','Casa e Decoração','Construção e Reforma','Eletrônicos e Tecnologia',
  'Veículos Elétricos','Carro e Motos','Gastronomia e Bebidas','Manutenção e Reparos',
  'Lazer e Diversão','Educação e Ensino'
];

export function CouponBrowser({ onCouponClick, defaultCity, defaultState }) {
  const [coupons, setCoupons] = useState([]);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState(defaultState || '');
  const [cityFilter, setCityFilter] = useState(defaultCity || '');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    axios.get(`${BACKEND_URL}/api/coupons`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }).then(res => setCoupons(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (defaultCity) setCityFilter(defaultCity);
    if (defaultState) setStateFilter(defaultState);
  }, [defaultCity, defaultState]);

  // Build city-state map for cascade logic
  const cityStateMap = {};
  coupons.forEach(c => { if (c.city && c.state) cityStateMap[c.city] = c.state; });
  const states = [...new Set(coupons.map(c => c.state).filter(Boolean))].sort();
  const cities = [...new Set(
    coupons
      .filter(c => !stateFilter || stateFilter === 'all' || c.state === stateFilter)
      .map(c => c.city)
      .filter(Boolean)
  )].sort();

  // Cascade handlers
  const handleStateChange = (val) => {
    setStateFilter(val);
    if (val && val !== 'all' && cityFilter && cityFilter !== 'all') {
      if (cityStateMap[cityFilter] !== val) setCityFilter('all');
    }
  };

  const handleCityChange = (val) => {
    setCityFilter(val);
    if (val && val !== 'all' && cityStateMap[val]) {
      setStateFilter(cityStateMap[val]);
    }
  };

  const filtered = coupons.filter(c => {
    const matchesSearch = !search || 
      (c.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.category || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.city || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase());
    const matchesCity = !cityFilter || cityFilter === 'all' || c.city === cityFilter;
    const matchesState = !stateFilter || stateFilter === 'all' || c.state === stateFilter;
    const matchesCategory = !categoryFilter || categoryFilter === 'all' || c.category === categoryFilter;
    return matchesSearch && matchesCity && matchesState && matchesCategory;
  }).sort((a, b) => {
    if (sortBy === 'highest_discount') return (b.discount_percentage || 0) - (a.discount_percentage || 0);
    if (sortBy === 'lowest_discount') return (a.discount_percentage || 0) - (b.discount_percentage || 0);
    if (sortBy === 'expiring_soon') return new Date(a.expires_at) - new Date(b.expires_at);
    return 0;
  });

  return (
    <div data-testid="coupon-browser">
      {/* Filters: Busca → Estado → Cidade → Categoria → Ordenação */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        {/* 1. Busca Livre */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar cupons..."
            className="pl-10" data-testid="coupon-search-input" />
        </div>
        {/* 2. Estado */}
        <Select value={stateFilter || undefined} onValueChange={handleStateChange}>
          <SelectTrigger data-testid="browser-state-filter"><SelectValue placeholder="Todos os estados" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* 3. Cidade (filtrada pelo estado) */}
        <Select value={cityFilter || undefined} onValueChange={handleCityChange}>
          <SelectTrigger data-testid="browser-city-filter"><SelectValue placeholder="Todas as cidades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as cidades</SelectItem>
            {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* 4. Categoria */}
        <Select value={categoryFilter || undefined} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="browser-category-filter"><SelectValue placeholder="Todas as categorias" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
        {/* 5. Ordenação */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="browser-sort-filter"><SelectValue placeholder="Ordenar" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Mais recentes</SelectItem>
            <SelectItem value="highest_discount">Maior desconto</SelectItem>
            <SelectItem value="lowest_discount">Menor desconto</SelectItem>
            <SelectItem value="expiring_soon">Expirando</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results */}
      {loading ? (
        <p className="text-sm text-slate-400 text-center py-6">Carregando cupons...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8">
          <Tag className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">{search || categoryFilter ? 'Nenhum cupom encontrado' : 'Nenhum cupom disponivel'}</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-3">{filtered.length} cupons disponiveis</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.slice(0, 12).map(c => (
              <Card key={c.coupon_id}
                className="bg-white border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onCouponClick?.(c)}
                data-testid={`browse-coupon-${c.coupon_id}`}>
                {c.images?.length > 0 && (
                  <div className="w-full aspect-[3/2] overflow-hidden rounded-t-lg">
                    <img src={c.images[0]} alt={c.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="pt-3 pb-3">
                  <div className="flex justify-between items-start mb-1">
                    <Badge className="bg-red-500 text-white border-0 text-xs">{c.discount_percentage}% OFF</Badge>
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{c.category}</Badge>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-0.5 line-clamp-1">{c.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-1 mb-1">{c.description}</p>
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <MapPin className="h-3 w-3" /> {c.city}{c.state ? `, ${c.state}` : ''}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
